import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { filter } from 'rxjs';
import { Menu, MenuItem } from '@model/identity';

const ICON_MENU_PREFIX = 'icon-menu-';
const ICON_DEFAULT = 'icon-menu-ordenes-ingresadas';
const ICON_AGREGAR_MUESTRA = 'icon-menu-agregar-muestra';
const ICON_PRE_INGRESOS = 'icon-menu-pre-ingresos';
const ICON_EXTRACCION = 'icon-menu-extraccion';
const ICON_EXPANSION_TRIPLETAS = 'icon-menu-expansion-tripletas';
const ICON_INFORMES = 'icon-menu-informes';
const ICON_HEADER_PREANALITICA = 'icon-menu-header-preanalitica';
const ICON_HEADER_LABORATORIO = 'icon-menu-header-laboratorio';

const ICON_FALLBACK_MAP: Record<string, string> = {
  'ORDENES INGRESADAS': ICON_DEFAULT,
  'ÓRDENES INGRESADAS': ICON_DEFAULT,
  'AGREGAR MUESTRA': ICON_AGREGAR_MUESTRA,
  'AGREGAR MUESTRAS': ICON_AGREGAR_MUESTRA,
  'PRE INGRESOS': ICON_PRE_INGRESOS,
  'PRE-INGRESOS': ICON_PRE_INGRESOS,
  PREINGRESOS: ICON_PRE_INGRESOS,
  EXTRACCIÓN: ICON_EXTRACCION,
  EXTRACCION: ICON_EXTRACCION,
  NGS: 'icon-menu-ngs',
  MICROARREGLOS: 'icon-menu-microarreglos',
  MLPA: 'icon-menu-mlpa',
  SANGER: 'icon-menu-sanger',
  'EXPANSIÓN DE TRIPLETAS': ICON_EXPANSION_TRIPLETAS,
  'EXPANSION DE TRIPLETAS': ICON_EXPANSION_TRIPLETAS,
  TRAZABILIDAD: 'icon-menu-trazabilidad',
  INFORMES: ICON_INFORMES,
  'INFORMES Y OBSERVACIONES': ICON_INFORMES,
  OBSERVACIONES: 'icon-menu-observaciones'
};

const HEADER_ICON_MAP: Record<string, string> = {
  PREANALITICA: ICON_HEADER_PREANALITICA,
  PRECLINICA: ICON_HEADER_PREANALITICA,
  LABORATORIO: ICON_HEADER_LABORATORIO,
  EXTRACCIÓN: ICON_HEADER_LABORATORIO,
  EXTRACCION: ICON_HEADER_LABORATORIO
};

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TooltipModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() minimized = false;
  @Input() seusMenus: Menu[] = [];
  @Output() minimizedChange = new EventEmitter<boolean>();

  public expandedMenus = new Set<number>();
  public expandedItems = new Set<number>();
  public showTooltip: string | null = null;
  public hoveredItem: number | null = null;

  constructor(public readonly router: Router) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateExpandedStateByRoute();
      });
  }

  ngOnInit(): void {
    this.checkSidebarMinimized();
    window.addEventListener('resize', this.checkSidebarMinimized.bind(this));
    this.updateExpandedStateByRoute();
  }

  ngOnChanges(): void {
    if (this.minimized) {
      this.expandedItems.clear();
    }
    if (this.seusMenus.length > 0 && this.expandedMenus.size === 0) {
      this.expandedMenus.add(0);
      this.navigateToFirstOptionIfNeeded();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.checkSidebarMinimized.bind(this));
  }

  private updateExpandedStateByRoute(): void {
    const currentUrl = this.router.url;
    this.seusMenus.forEach((menu, menuIndex) => {
      menu.menuItems.forEach((item) => {
        if (this.isItemOrChildActive(item, currentUrl)) {
          this.expandedMenus.add(menuIndex);
          if (item.children && item.children.length > 0) {
            this.expandedItems.add(item.cdresource);
          }
        }
      });
    });
  }

  private navigateToFirstOptionIfNeeded(): void {
    const currentUrl = this.router.url;
    const isRootRoute =
      currentUrl === '/' ||
      currentUrl === '/home' ||
      currentUrl === '' ||
      currentUrl.startsWith('/?');

    if (isRootRoute && this.seusMenus.length > 0) {
      const firstMenu = this.seusMenus[0];
      if (firstMenu.menuItems && firstMenu.menuItems.length > 0) {
        const firstItem = firstMenu.menuItems[0];
        if (firstItem.target) {
          void this.router.navigate([firstItem.target]);
        }
      }
    }
  }

  private isItemOrChildActive(item: MenuItem, currentUrl: string): boolean {
    if (item.target && currentUrl.startsWith(item.target)) {
      return true;
    }
    if (item.children) {
      return item.children.some(
        (child) => child.target && currentUrl.startsWith(child.target)
      );
    }
    return false;
  }

  toggleSidebar(): void {
    this.minimizedChange.emit(!this.minimized);
  }

  toggleMenu(index: number): void {
    if (this.expandedMenus.has(index)) {
      this.expandedMenus.delete(index);
    } else {
      this.expandedMenus.clear();
      this.expandedMenus.add(index);
    }
  }

  getExpandedMenuName(): string {
    if (this.expandedMenus.size === 0 || this.seusMenus.length === 0) {
      return 'SIG';
    }
    const expandedIndex = Array.from(this.expandedMenus)[0];
    return this.seusMenus[expandedIndex]?.name || 'SIG';
  }

  getHeaderIcon(): string {
    const menuName = this.getExpandedMenuName().toUpperCase();

    for (const [key, icon] of Object.entries(HEADER_ICON_MAP)) {
      if (menuName.includes(key)) {
        return icon;
      }
    }

    return ICON_HEADER_LABORATORIO;
  }

  toggleItem(item: MenuItem): void {
    if (this.minimized) {
      return;
    }

    if (this.expandedItems.has(item.cdresource)) {
      this.expandedItems.delete(item.cdresource);
    } else {
      this.expandedItems.add(item.cdresource);
    }
  }

  hasChildren(item: MenuItem): boolean {
    return !!(item.children && item.children.length > 0);
  }

  isMenuExpanded(index: number): boolean {
    return this.expandedMenus.has(index);
  }

  isItemExpanded(item: MenuItem): boolean {
    return this.expandedItems.has(item.cdresource);
  }

  isItemActive(item: MenuItem): boolean {
    return item.target ? this.router.url.startsWith(item.target) : false;
  }

  hasValidTarget(item: MenuItem): boolean {
    const hasTarget = !!(item.target && item.target.trim() !== '');
    const hasChildren = !!(item.children && item.children.length > 0);

    if (hasChildren) {
      return false;
    }

    return hasTarget;
  }

  navigateTo(item: MenuItem): void {
    if (item.target) {
      void this.router.navigate([item.target]).catch((error) => {
        console.error('Error navigating:', error);
      });
    }
  }

  onItemClick(item: MenuItem, event: Event): void {
    if (item.children && item.children.length > 0) {
      this.toggleItem(item);
      event.preventDefault();
    } else if (item.target) {
      this.navigateTo(item);
    }
  }

  getItemIcon(item: MenuItem): string {
    if (item.image && item.image.trim() !== '') {
      const image = item.image.trim();
      if (image.startsWith(ICON_MENU_PREFIX)) {
        return image;
      }
      return `${ICON_MENU_PREFIX}${image}`;
    }

    const itemNameUpper = item.name.toUpperCase();
    if (ICON_FALLBACK_MAP[itemNameUpper]) {
      return ICON_FALLBACK_MAP[itemNameUpper];
    }

    return ICON_DEFAULT;
  }

  getModuleTitle(): string {
    if (this.seusMenus.length > 0) {
      return this.seusMenus[0].name;
    }
    return 'SIG';
  }

  private checkSidebarMinimized(): void {
    if (window.innerWidth <= 1450) {
      if (!this.minimized) {
        this.minimized = true;
        this.minimizedChange.emit(true);
      }
    } else if (this.minimized) {
      this.minimized = false;
      this.minimizedChange.emit(false);
    }
  }
}
